import { VoidCallback } from '@deqo/foundation';
import React, { useCallback, useState } from 'react';
import { LFC } from '../../Legacy';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon, NoIcon } from '../../concepts/resources/Icon';
import { useResolve } from '../Container';
import { AccountModel } from '../account/AccountModel';
import { Crumbs } from '../common/Crumbs';
import { ShowIfHasFeature } from '../common/FeatureElements';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { PopupManager } from '../popups/PopupManager';
import { Menu } from './../../ui';
import { BoardInfo } from './BoardInfo';
import { setBoardName } from './CoreCommands';
import { EditorSession, useEditorSessionObserver } from './EditorSession';
import { MyRoleInfo } from './MyRoleInfo';
import { manageAccessPopup } from './participants/ManageAccessPopup';

const ActionMenu: LFC<{ onEditName: VoidCallback }> = ({ onEditName }) => {

    const account = useResolve(AccountModel);
    const dispatch = useResolve(CommandDispatcher);
    const popups = useResolve(PopupManager);
    const board = useEditorSessionObserver(BoardInfo);
    const { canParticipate, canManage } = useEditorSessionObserver(MyRoleInfo);

    const [open, setOpen] = useState(false);
    const close = () => setOpen(false);

    // useEffectOnce(() => void (dispatch('importData')));

    if (canParticipate && !account.plan.features.readonly) {
        return <Flexer data-tour="board-menu" >
            <Crumbs.Menu label={board.label} open={open} setOpen={setOpen}>
                <Flexer width="14">
                    <Menu>
                        {canManage && <>
                            <Menu.Item data-action="header.boardMenu.rename" role="menuitem" aria-label="Rename Board" onClick={onEditName}>
                                <Icon size="1.2" src="deqo://icons/hero/pencil-alt" />
                                Rename Board
                            </Menu.Item>
                            <Menu.Item data-action="header.boardMenu.openManageAccess" role="menuitem" aria-label="Manage Access" onClick={() => popups.invoke(manageAccessPopup()) && close()}>
                                <Icon size="1.2" src="deqo://icons/hero/lock-closed" />
                                Manage Access
                            </Menu.Item>
                            <Menu.Divide />
                        </>}
                        <ShowIfHasFeature name="jiraBeta">
                            <Menu.Item data-action="header.boardMenu.importData" onClick={() => dispatch('importData') && close()}>
                                <Icon size="1.2" src="deqo://icons/hero/download" />
                                Import Data
                            </Menu.Item>
                        </ShowIfHasFeature>
                        <Menu.Item data-action="header.boardMenu.exportData" onClick={() => dispatch('exportData') && close()}>
                            <Icon size="1.2" src="deqo://icons/hero/upload" />
                            Export Data
                        </Menu.Item>
                        <Menu.Item data-action="header.boardMenu.exportPdf" role="menuitem" aria-label="Export PDF" onClick={() => dispatch('exportPdf') && close()}>
                            <NoIcon size="1.2" />
                            Export PDF
                        </Menu.Item>
                    </Menu>
                </Flexer>
            </Crumbs.Menu>
        </Flexer>;
    }

    return <Crumbs.Title>
        {board.label}
    </Crumbs.Title>;
};

const NameInput: LFC<{ onDone }> = ({ onDone }) => {

    const dispatch = useResolve(CommandDispatcher);
    const board = useResolve(EditorSession).get(BoardInfo);
    const [label, setLabel] = useState(board.label);

    const onApply = useCallback((value: string) => {
        dispatch(setBoardName, [value]);
        setLabel(value);
        onDone();
    }, [onDone]);

    return <Crumbs.Input initialValue={label} onApply={onApply} onCancel={onDone} />;
};

export const EditorCrumbsWithMenu = () => {
    const [editing, setEditing] = useState(false);
    return <Crumbs>
        {!editing &&
            <ActionMenu onEditName={() => setEditing(true)} />
        }
        {editing &&
            <NameInput onDone={() => setEditing(false)} />
        }
    </Crumbs>;
};
